import { FC, ReactNode } from 'react';
import { ProductCardContainer } from './styles';
import ProductCardImageContainer from './product-card-image-container';
import ProductContentContainer from './product-card-content-container';
import { ActionObject } from 'lib/product-actions';

export interface ProductCardProps {
  id?: string;
  className?: string;
  showNewPill: boolean;
  newPillText?: string;
  newPillColor?:
    | 'seedgreen'
    | 'snowwhite'
    | 'amprimary'
    | 'pmprimary'
    | 'dmprimary';
  newPillFontColor?: 'seedgreen' | 'snowwhite';
  showDiscountPill: boolean;
  showCapsuleImage: boolean;
  capsuleImageUrl: string;

  imageClickAction?: () => void;
  isFeatured?: boolean;

  primaryAction?: ActionObject | null;
  secondaryAction?: ActionObject | null;

  styleConfig: {
    // Layout
    contentPosition?: 'left' | 'right';
    mobileLayout?: 'horizontal' | 'vertical';
    flexDirection?: string;
    flexDirectionDesktop?: string;

    // Styling
    backgroundColor?: string;
    textColor?: 'snowwhite' | 'seedgreen';
    borderRadius?: string;
    padding?: string;
    gap?: string;

    // Container constraints
    width?: string;
    maxWidth?: string;
    minWidth?: string;
    height?: string;
    maxHeight?: string;
    minHeight?: string;
    overflow?: string;
  };

  productData: {
    productName: string;
    productSku: string;
    productDescription: string;
    productAlphaCode: string;
    productAlphaCodeTrademark?: boolean;
    productAlphaCodeRegistered?: boolean;
    productImageUrl: string;
    price: string;
    salePrice?: string | null;
    discountText?: string;
    pillColor?: string;
    pillPosition?:
      | 'left'
      | 'right'
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right'
      | 'center'
      | 'top-center'
      | 'bottom-center';
    productNumberOneProbioticImage?: string;
    productNumberOneProbioticAltText?: string;
    productNumberOneProbioticDisclaimer?: string;
    productNumberOneProbioticDisclaimerMobile?: string;
  };

  imageConfig: {
    // styling
    padding?: string;
    backgroundColor?: string;

    // flexbox layout
    flexGrow?: number;
    flexShrink?: number;
    flexBasis?: string;

    // image sizing (for Next.js Image component)
    imageWidth?: number;
    imageHeight?: number;
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    objectPosition?: string;

    // container sizing
    containerWidth?: string;
    containerHeight?: string;
    containerMinHeight?: string;
    containerMaxWidth?: string;
    containerMaxHeight?: string;

    // mobile-specific overrides
    mobileContainerWidth?: string;
    mobileContainerHeight?: string;

    // Link URL to make image clickable
    linkUrl?: string;
  };

  // Content container flexbox config
  contentConfig: {
    flexGrow?: number;
    flexShrink?: number;
    flexBasis?: string;
    height?: string;
    width?: string;
    padding?: string;

    // Theme props
    textColor?: 'snowwhite' | 'seedgreen';

    // Action container config
    actionConfig?: {
      gap?: string;
      alignItems?: string;
      flexWrap?: string;
      buttonWidth?: string;
      buttonFlexGrow?: number;
    };
    // Price container config
    priceConfig?: {
      gap?: string;
    };
  };

  children?: ReactNode;
}

const ProductCard: FC<ProductCardProps> = ({
  id,
  className,
  showNewPill = false,
  newPillText = 'New',
  newPillFontColor,
  showDiscountPill = false,
  imageClickAction,
  showCapsuleImage,
  capsuleImageUrl,
  isFeatured = false,
  primaryAction,
  secondaryAction,

  productData,

  imageConfig,
  contentConfig,
  styleConfig,

  children,
}) => {
  const {
    productName,
    productSku,
    productDescription,
    productNumberOneProbioticImage,
    productNumberOneProbioticAltText,
    productNumberOneProbioticDisclaimer,
    productNumberOneProbioticDisclaimerMobile,
    productAlphaCode,
    productAlphaCodeTrademark,
    productAlphaCodeRegistered,
    productImageUrl,
    price,
    salePrice,
    discountText,
    pillColor,
    pillPosition,
  } = productData;

  const imageLabels = showNewPill
    ? [
        {
          text: newPillText,
          fontColor: newPillFontColor ?? 'snowwhite',
          pillColor,
          position: pillPosition || 'left',
        },
      ]
    : [];

  const badgeColor: 'seedgreen' | 'snowwhite' =
    styleConfig?.textColor || 'seedgreen';
  const badges = productAlphaCode
    ? [
        {
          text: productAlphaCode,
          trademark: productAlphaCodeTrademark,
          registered: productAlphaCodeRegistered,
          color: badgeColor,
          type: 'pill' as const,
        },
      ]
    : [];

  return (
    <ProductCardContainer id={id} className={className} config={styleConfig}>
      {showCapsuleImage && (
        <ProductCardImageContainer
          src={capsuleImageUrl}
          alt={`${productName} capsule`}
          className='capsule-image'
          styleConfig={imageConfig}
        />
      )}

      <ProductCardImageContainer
        id={`product-card-image-container-${productSku}`}
        className='product-card-image-container'
        src={productImageUrl}
        alt={productName}
        labels={imageLabels}
        onClick={imageClickAction}
        styleConfig={imageConfig}
      />

      <ProductContentContainer
        id={`product-card-content-container-${productSku}`}
        className={'product-card-content-container'}
        title={productName}
        description={productDescription}
        numberOneProbioticImage={productNumberOneProbioticImage}
        numberOneProbioticAltText={productNumberOneProbioticAltText}
        numberOneProbioticDisclaimer={productNumberOneProbioticDisclaimer}
        numberOneProbioticDisclaimerMobile={
          productNumberOneProbioticDisclaimerMobile
        }
        badges={badges}
        price={price}
        salePrice={salePrice}
        showDiscount={showDiscountPill}
        discountText={discountText}
        isFeatured={isFeatured}
        styleConfig={{
          ...(contentConfig || {}),
          textColor: styleConfig?.textColor ?? contentConfig?.textColor,
        }}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      />
      {children}
    </ProductCardContainer>
  );
};

export default ProductCard;
